import { useMemo } from 'react'
import { useLoaderData } from '@remix-run/react'
import { parse, stringify } from 'flatted'

import {
  checkIfSlugRequiresRedirect,
  urlInRedirectList,
  getArticleBySlug,
  getArticlesByCategory,
} from '~/queries/getArticle'
import { BlogPosts } from '~/components/blog/BlogPosts'
import { categoryGroupPosts } from '~/components/blog/BlogPosts/utils'
import { BlogPaginate } from '~/components/blog/BlogPaginate'
import { Blog } from '~/components/blog/Blog'
import { Layout } from '~/components/Layout'
import { getCategories } from '~/queries/getCategories'
import { CategoryHero } from '~/components/blog/CategoryHero'
import { Article } from '~/components/blog/Article'

import { redirect, type LoaderFunction, type MetaFunction } from '@remix-run/node'
import {
  PageContext,
  preparePageContext,
  prepareRemixRequest,
  prepareRobotsMetadata,
  previewRouteHandler,
} from '~/utils/routes/routeHelper'
import {
  TypeArticle,
  TypeArticleSkeleton,
  TypeBlogCategorySkeleton,
  TypeCategorySortingSkeleton,
} from '~/types/contentful'
import { Entry, EntryCollection } from 'contentful'

export type SingleArticlePageData = {
  type: 'article'
  article: string
  contextValue: PageContext
}

export type CategoryArticlePageData = {
  type: 'category'
  articleEntries: EntryCollection<TypeArticleSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  currentCategory?: Entry<TypeBlogCategorySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  categoryEntries?: Entry<TypeCategorySortingSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  contextValue: PageContext
}

export type CareerAdviceLoaderData = SingleArticlePageData | CategoryArticlePageData

export const loader: LoaderFunction = async ({ params, request }) => {
  if (!params.slug) {
    throw new Response('Not Found', { status: 404 })
  }
  const url = prepareRemixRequest(request)

  const { preview, searchParams } = previewRouteHandler(url)
  const page = searchParams['page']
  const contextValue = preparePageContext(url)

  const [categoryEntries, requiresRedirect, inRedirectList, articleEntries] = await Promise.all([
    // get categories
    getCategories(preview),
    // old redirect object. to be removed
    checkIfSlugRequiresRedirect({ slug: params.slug, preview }),
    // urls to be redirected to the new article
    urlInRedirectList({ slug: params.slug, preview }),
    // article information
    getArticleBySlug({ slug: params.slug, preview }),
  ])

  const categories = categoryEntries?.items[0]?.fields?.categories as Entry<
    TypeBlogCategorySkeleton,
    'WITHOUT_UNRESOLVABLE_LINKS',
    string
  >[]
  const currentCategory =
    Array.isArray(categories) && categories.find((item) => item?.fields?.slug === params.slug)

  if (currentCategory) {
    const articleEntries = await getArticlesByCategory({
      category: params.slug,
      page: Number(page || '1'),
      perPage: 19,
      preview,
    })

    if (!articleEntries || articleEntries?.items?.length == 0) {
      throw new Response('Not Found', { status: 404 })
    }

    return {
      type: 'category',
      articleEntries,
      categoryEntries: categoryEntries?.items[0],
      currentCategory,
      contextValue,
    } satisfies CategoryArticlePageData
  }

  /* ARTICLE */

  if (inRedirectList) {
    const articleEntry = inRedirectList.fields.article as Entry<
      TypeArticleSkeleton,
      undefined,
      string
    >

    const redirectArticle = inRedirectList.fields.action
    if (articleEntry && redirectArticle)
      return redirect(`/career-advice/${articleEntry.fields.slug}${preview ? '?preview' : ''}`, 301)
    else if (articleEntry) {
      return {
        type: 'article',
        article: stringify(articleEntry),
        contextValue,
      } satisfies SingleArticlePageData
    }
  }

  if (requiresRedirect) {
    return redirect(
      `/career-advice/${requiresRedirect[0].fields.newSlug}${preview ? '?preview' : ''}`,
      301,
    )
  }

  if (!articleEntries || articleEntries.items.length === 0) {
    throw new Response('Not Found', { status: 404 })
  }

  const redirect301 = articleEntries.includes?.Entry?.find((e) => {
    return e.sys?.contentType?.sys?.id === 'redirectUrl' && e.fields?.oldSlug === params.slug
  })

  if (
    redirect301 &&
    redirect301.fields?.redirectType === '301 Redirect' &&
    redirect301.fields?.newSlug !== params.slug &&
    redirect301.fields.oldSlug === params.slug
  ) {
    return redirect(`/career-advice/${redirect301.fields?.newSlug}`, 301)
  }

  return {
    type: 'article',
    article: stringify(articleEntries.items[0]),
    contextValue,
  } satisfies SingleArticlePageData
}

export const meta: MetaFunction<typeof loader> = (props) => {
  const { data }: { data: CareerAdviceLoaderData } = props

  const { contextValue } = data
  const metaRobots = prepareRobotsMetadata(contextValue?.url)

  if (data.type == 'article') {
    const article = data.article

    let articleInfo
    // workaround for flatted strange case where it still returns an object
    if (typeof article == 'string') {
      articleInfo = parse(article)
    }

    const { metaTitle, metaDescription } = articleInfo?.fields || {}
    return [
      {
        title: metaTitle ? `${metaTitle} · Career.io` : undefined,
      },
      { content: metaDescription, name: 'description' },
      { name: 'robots', content: metaRobots },
    ]
  } else {
    const { articleEntries, currentCategory } = data

    if (currentCategory) {
      const { metaTitle, metaDescription } = currentCategory?.fields || {
        metaTitle: '',
        metaDescription: '',
      }

      return [
        {
          title: metaTitle ? `${metaTitle} · Career.io` : undefined,
        },
        { content: metaDescription, name: 'description' },
        { name: 'robots', content: metaRobots },
      ]
    }

    const { metaTitle, metaDescription } = articleEntries?.items[0]?.fields || {}
    return [
      {
        title: metaTitle ? `${metaTitle} · Career.io` : undefined,
      },
      { content: metaDescription, name: 'description' },
      { name: 'robots', content: metaRobots },
    ]
  }
}

export default function BlogInnerPage() {
  const data = useLoaderData<typeof loader>() as CareerAdviceLoaderData

  if (data.type == 'article') {
    const articleEntry: TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string> = parse(data.article)
    return <CareerAdviceSingleArticle article={articleEntry} />
  } else {
    const { articleEntries } = data

    const { categoryEntries, currentCategory } = data

    const postGroups = useMemo(
      () => categoryGroupPosts(articleEntries.items),
      [articleEntries.items],
    )

    return (
      <CareerAdviceCategoryPage
        articleEntries={articleEntries}
        categoryEntries={categoryEntries}
        currentCategory={currentCategory}
        postGroups={postGroups}
      />
    )
  }
}

type CareerAdviceSingleArticleProps = {
  article: TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

/**
 * Layout of the page when it is a single article to be shown
 * @param
 * @returns
 */
const CareerAdviceSingleArticle = ({ article }: CareerAdviceSingleArticleProps) => {
  return (
    <Layout>
      <Layout.Content>
        <Article data={article} />
      </Layout.Content>
    </Layout>
  )
}

type CareerAdviceCategoryPageProps = {
  categoryEntries?: Entry<TypeCategorySortingSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  currentCategory?: Entry<TypeBlogCategorySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  postGroups: Array<
    | TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string>[]
    | TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string>
  > // grouped posts for the category page
  articleEntries: EntryCollection<TypeArticleSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
}

/**
 *
 * Layout for the cases where it is the career advice category page
 * @returns
 */
const CareerAdviceCategoryPage = ({
  categoryEntries,
  currentCategory,
  postGroups,
  articleEntries,
}: CareerAdviceCategoryPageProps) => {
  return (
    <Layout
      isFlatHeader={!categoryEntries}
      headerBgColor={currentCategory?.fields.color?.toString()}
    >
      <Layout.Content>
        {categoryEntries ? (
          <Blog categories={categoryEntries}>
            {currentCategory && <CategoryHero data={currentCategory} />}
            <BlogPosts
              postGroups={postGroups}
              categories={categoryEntries}
              isCategoryPage={Boolean(currentCategory)}
            />
            <BlogPaginate total={articleEntries.total} perPage={19} />
          </Blog>
        ) : (
          <Article data={articleEntries.items[0]} />
        )}
      </Layout.Content>
    </Layout>
  )
}
